import { zipSync, unzipSync, strToU8, strFromU8 } from 'fflate'
import { exportDB, getLancamentos, getDiario, getObraConfig } from './crud.js'
import { db, ALL_TABLES, type Documento } from './db.js'
import {
  clearAllDocumentFiles,
  readDocumentBlob,
  restoreDocumentoArquivo,
} from './documentStorage.js'
import { formatCurrency, formatDate, formatDatetime } from '@utils/format.js'
import modulosJson from '@content/modulos.json'

// ── Rastreamento de último backup ─────────────────────────────────

const BACKUP_KEY = 'minha-obra-last-backup'
const BACKUP_DIAS_ALERTA = 7

export function getDataUltimoBackup(): string | null {
  try {
    return localStorage.getItem(BACKUP_KEY)
  } catch {
    return null
  }
}

function marcarBackup() {
  try {
    localStorage.setItem(BACKUP_KEY, new Date().toISOString())
  } catch {
    /* noop */
  }
}

export function diasSemBackup(): number | null {
  const last = getDataUltimoBackup()
  if (!last) return null
  const diff = Date.now() - new Date(last).getTime()
  return Math.floor(diff / (1000 * 60 * 60 * 24))
}

export function precisaBackup(): boolean {
  const dias = diasSemBackup()
  return dias === null || dias >= BACKUP_DIAS_ALERTA
}

// ── Helpers de download ───────────────────────────────────────────

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function abrirJanelaPrint(titulo: string, html: string) {
  if (typeof window === 'undefined') return
  const win = window.open('', '_blank')
  if (!win) return
  win.document.write(`<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>${titulo}</title>
  <style>
    /* impeccable-disable design-system-color, design-system-radius -- standalone print export; token palette */
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'DM Sans', system-ui, sans-serif; font-size: 12px; color: #1a1814; padding: 24px; }
    h1 { font-size: 18px; margin-bottom: 4px; }
    h2 { font-size: 14px; margin: 16px 0 6px; border-bottom: 1px solid #dedad0; padding-bottom: 4px; }
    p.sub { font-size: 11px; color: #534e42; margin-bottom: 16px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
    th { background: #efede7; text-align: left; padding: 5px 8px; font-size: 11px; border: 1px solid #dedad0; }
    td { padding: 5px 8px; border: 1px solid #dedad0; font-size: 11px; vertical-align: top; }
    tr:nth-child(even) td { background: #f4f2ec; }
    .summary { display: flex; gap: 24px; margin-bottom: 16px; flex-wrap: wrap; }
    .summary-card { border: 1px solid #dedad0; border-radius: 12px; padding: 10px 16px; min-width: 140px; }
    .summary-card__label { font-size: 10px; color: #534e42; margin-bottom: 2px; }
    .summary-card__value { font-size: 16px; font-weight: bold; }
    .entry { border-bottom: 1px solid #efede7; padding: 10px 0; }
    .entry__meta { font-size: 10px; color: #534e42; margin-bottom: 4px; }
    .entry__texto { white-space: pre-wrap; line-height: 1.5; }
    .badge { display: inline-block; background: #efede7; border-radius: 12px; padding: 1px 6px; font-size: 10px; }
    .badge--ok { background: #e8f6ec; color: #156628; }
    .badge--andamento { background: #fff6e0; color: #95680a; }
    .badge--pendente { background: #fceaec; color: #861a28; }
    .progress { height: 6px; background: #dedad0; border-radius: 999px; margin: 6px 0 10px; overflow: hidden; }
    .progress__fill { height: 100%; background: #1b3a6b; border-radius: 999px; }
    .modulo__meta { font-size: 11px; color: #534e42; margin: 2px 0 4px; }
    .summary-overall { font-size: 13px; font-weight: 600; margin-bottom: 16px; padding: 10px 14px; background: #efede7; border-radius: 12px; }
    @media print { body { padding: 0; } }
  </style>
</head>
<body>${html}<script>window.onload = () => { window.print(); }</script></body>
</html>`)
  win.document.close()
}

// ── Backup ZIP (dados + arquivos anexados) ────────────────────────

type ManifestDocumento = Omit<Documento, 'id'> & { arquivoRef?: string; idOriginal?: number }

/**
 * Manifest do backup. v1 gravava só as tabelas iniciais; v2 grava todas as tabelas
 * de dados (`ALL_TABLES` exceto blobs) usando o nome da tabela como chave.
 */
interface BackupManifest extends Partial<Record<string, unknown>> {
  versao: number
  exportadoEm: string
  documentos: ManifestDocumento[]
}

const BACKUP_VERSAO = 2
const MANIFEST_FILE = 'manifest.json'
const ARQUIVOS_PREFIX = 'arquivos/'
const TABELAS_DADOS = ALL_TABLES.filter((t) => t !== 'documento_arquivos' && t !== 'documentos')

export async function exportarBackup(): Promise<void> {
  const data = await exportDB()
  const docs = data.documentos as Documento[]
  const manifest: BackupManifest = {
    versao: BACKUP_VERSAO,
    exportadoEm: new Date().toISOString(),
    documentos: [],
  }
  for (const tabela of TABELAS_DADOS) manifest[tabela] = data[tabela] ?? []

  const zipEntries: Record<string, Uint8Array> = {}

  for (const doc of docs) {
    const { id, ...meta } = doc
    const entry: ManifestDocumento = { ...meta, ...(id != null ? { idOriginal: id } : {}) }

    if (doc.hasArquivo && id != null) {
      const blob = await readDocumentBlob(id, doc.arquivoBackend)
      if (blob) {
        const ref = String(id)
        entry.arquivoRef = ref
        zipEntries[`${ARQUIVOS_PREFIX}${ref}`] = new Uint8Array(await blob.arrayBuffer())
      }
    }

    manifest.documentos.push(entry)
  }

  zipEntries[MANIFEST_FILE] = strToU8(JSON.stringify(manifest, null, 2))
  const zipped = zipSync(zipEntries, { level: 6 })
  const stamp = new Date().toISOString().slice(0, 10)
  downloadBlob(new Blob([zipped], { type: 'application/zip' }), `minha-obra-backup-${stamp}.zip`)
  marcarBackup()
}

export async function importarBackup(file: File): Promise<void> {
  const buffer = new Uint8Array(await file.arrayBuffer())
  const entries = unzipSync(buffer)
  const manifestRaw = entries[MANIFEST_FILE]
  if (!manifestRaw) throw new Error('Backup inválido: manifest.json ausente')

  const manifest = JSON.parse(strFromU8(manifestRaw)) as BackupManifest
  const tabelas = ALL_TABLES.map((name) => db.table(name))

  await db.transaction('rw', tabelas, async () => {
    await clearAllDocumentFiles()
    await Promise.all(tabelas.map((t) => t.clear()))

    for (const tabela of TABELAS_DADOS) {
      const rows = manifest[tabela]
      if (Array.isArray(rows) && rows.length) await db.table(tabela).bulkAdd(rows)
    }

    // Documentos: ids podem mudar na importação; mantém vínculo das fotos do diário.
    const idMap = new Map<number, number>()
    for (const doc of manifest.documentos ?? []) {
      const { arquivoRef, idOriginal, ...meta } = doc
      const newId = await db.documentos.add({
        ...meta,
        hasArquivo: Boolean(arquivoRef),
        criadoEm: meta.criadoEm || new Date().toISOString(),
      })
      if (idOriginal != null) idMap.set(idOriginal, newId)

      if (arquivoRef) {
        const bytes = entries[`${ARQUIVOS_PREFIX}${arquivoRef}`]
        if (bytes) {
          const blob = new Blob([bytes], {
            type: meta.tipo || 'application/octet-stream',
          })
          await restoreDocumentoArquivo(newId, blob, meta.tipo, meta.tamanho ?? bytes.byteLength)
        }
      }
    }

    if (idMap.size > 0) {
      const entradas = await db.diario.toArray()
      for (const e of entradas) {
        if (!e.fotoIds?.length || e.id == null) continue
        const fotoIds = e.fotoIds.map((id) => idMap.get(id) ?? id)
        await db.diario.update(e.id, { fotoIds })
      }
    }
  })
}

/** @deprecated use exportarBackup */
export const exportarBackupJSON = exportarBackup

/** @deprecated use importarBackup */
export const importarBackupJSON = importarBackup

// ── RFC-11.3 · Relatório financeiro (print to PDF) ────────────────

export async function exportarRelatorioFinanceiro(): Promise<void> {
  const [lancamentos, config] = await Promise.all([getLancamentos(), getObraConfig()])

  const totalGasto = lancamentos
    .filter((l) => l.tipo !== 'receita')
    .reduce((s, l) => s + l.valor, 0)
  const totalReceita = lancamentos
    .filter((l) => l.tipo === 'receita')
    .reduce((s, l) => s + l.valor, 0)
  const orcamento = config?.orcamentoTotal ?? 0
  const saldo = orcamento - totalGasto

  const linhasLancamentos = lancamentos
    .sort((a, b) => b.data.localeCompare(a.data))
    .map(
      (l) => `
      <tr>
        <td>${formatDate(l.data)}</td>
        <td>${l.titulo}</td>
        <td>${l.categoria}</td>
        <td>${l.fornecedor ?? '—'}</td>
        <td style="text-align:right">${formatCurrency(l.valor)}</td>
        <td><span class="badge">${l.status}</span></td>
      </tr>`,
    )
    .join('')

  const html = `
    <h1>📊 Relatório Financeiro</h1>
    <p class="sub">${config?.nome ?? 'Obra'} · Gerado em ${formatDatetime(new Date().toISOString())}</p>
    <div class="summary">
      <div class="summary-card"><div class="summary-card__label">Orçamento</div><div class="summary-card__value">${formatCurrency(orcamento)}</div></div>
      <div class="summary-card"><div class="summary-card__label">Total gasto</div><div class="summary-card__value">${formatCurrency(totalGasto)}</div></div>
      <div class="summary-card"><div class="summary-card__label">Receitas</div><div class="summary-card__value">${formatCurrency(totalReceita)}</div></div>
      <div class="summary-card"><div class="summary-card__label">Saldo</div><div class="summary-card__value">${formatCurrency(saldo)}</div></div>
    </div>
    <h2>Todos os Lançamentos (${lancamentos.length})</h2>
    <table>
      <thead><tr><th>Data</th><th>Descrição</th><th>Categoria</th><th>Fornecedor</th><th>Valor</th><th>Status</th></tr></thead>
      <tbody>${linhasLancamentos}</tbody>
    </table>`

  abrirJanelaPrint('Relatório Financeiro', html)
}

// ── RFC-11.4 · Checklist geral como PDF (print) ───────────────────

export async function exportarChecklistPDF(): Promise<void> {
  const config = await getObraConfig()
  const topicos = await db.topicos_progresso.toArray()
  type ModuloRaw = {
    id: string
    titulo: string
    icone: string
    topicos: { id: string; titulo: string }[]
  }
  const modulos = (modulosJson as { modulos: ModuloRaw[] }).modulos

  const secoes = modulos
    .map((m) => {
      const concluidos = topicos.filter((t) => t.moduloId === m.id && t.concluido).length
      const total = m.topicos.length
      const pct = total > 0 ? Math.round((concluidos / total) * 100) : 0

      const linhas = m.topicos
        .map((t) => {
          const prog = topicos.find((tp) => tp.topicoId === t.id)
          const status = prog?.concluido
            ? 'ok'
            : prog && prog.percentualPratico > 0
              ? 'andamento'
              : 'pendente'
          const label = prog?.concluido
            ? 'Concluído'
            : status === 'andamento'
              ? 'Em andamento'
              : 'Pendente'
          return `<tr><td>${t.titulo}</td><td><span class="badge badge--${status}">${label}</span></td></tr>`
        })
        .join('')

      return `
      <h2>${m.icone} ${m.titulo} · ${concluidos}/${total} (${pct}%)</h2>
      <table><thead><tr><th>Tópico</th><th>Status</th></tr></thead><tbody>${linhas}</tbody></table>`
    })
    .join('')

  const html = `
    <h1>✅ Checklist Geral da Obra</h1>
    <p class="sub">${config?.nome ?? 'Obra'} · Gerado em ${formatDatetime(new Date().toISOString())}</p>
    ${secoes}`

  abrirJanelaPrint('Checklist Geral', html)
}

// ── RFC-11.5 · Diário como PDF (print) ───────────────────────────

const CLIMA_LABEL: Record<string, string> = {
  sol: 'Sol',
  nublado: 'Nublado',
  chuva: 'Chuva',
  'chuva-forte': 'Chuva forte',
}

async function blobParaDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}

export async function exportarDiarioPDF(opcoes: { comFotos?: boolean } = {}): Promise<void> {
  const comFotos = opcoes.comFotos ?? true
  const [entradas, config, contatos] = await Promise.all([
    getDiario(),
    getObraConfig(),
    db.contatos.toArray(),
  ])
  const nomePorId = new Map(contatos.map((c) => [c.id as number, c.nome]))

  const blocos: string[] = []
  for (const e of entradas) {
    const meta: string[] = [formatDatetime(e.criadoEm)]
    if (e.clima) meta.push(CLIMA_LABEL[e.clima] ?? e.clima)
    if (e.equipeIds?.length) {
      meta.push(`Equipe: ${e.equipeIds.map((id) => nomePorId.get(id) ?? '?').join(', ')}`)
    }

    let fotosHtml = ''
    if (comFotos && e.fotoIds?.length) {
      const docs = await db.documentos.bulkGet(e.fotoIds)
      const imgs: string[] = []
      for (const d of docs) {
        if (!d?.id || !d.hasArquivo) continue
        const blob = await readDocumentBlob(d.id, d.arquivoBackend)
        if (!blob) continue
        if (d.tipo.startsWith('video/')) {
          imgs.push(
            `<p class="entry__video">${escapeHtml(d.nome || 'Vídeo anexado')} (vídeo não entra no PDF)</p>`,
          )
          continue
        }
        if (!d.tipo.startsWith('image/')) continue
        imgs.push(`<img class="entry__foto" src="${await blobParaDataUrl(blob)}" alt="">`)
      }
      if (imgs.length) fotosHtml = `<div class="entry__fotos">${imgs.join('')}</div>`
    }

    blocos.push(`
    <div class="entry">
      <div class="entry__meta">${meta.map(escapeHtml).join(' · ')}</div>
      <div class="entry__texto">${escapeHtml(e.texto)}</div>
      ${fotosHtml}
    </div>`)
  }

  const html = `
    <style>
      .entry__fotos { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
      .entry__foto { width: 31%; max-height: 160px; object-fit: cover; border-radius: 6px; }
      .entry__video { width: 100%; margin: 0; font-size: 12px; color: #5c5346; }
    </style>
    <h1>📓 Diário de Bordo</h1>
    <p class="sub">${escapeHtml(config?.nome ?? 'Obra')} · ${entradas.length} anotações · Gerado em ${formatDatetime(new Date().toISOString())}</p>
    ${blocos.join('')}`

  abrirJanelaPrint('Diário de Bordo', html)
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

type ModuloProgressoExport = {
  titulo: string
  icone: string
  pct: number
  concluidos: number
  total: number
  topicos: { titulo: string; status: 'concluido' | 'em-andamento' | 'pendente' }[]
}

// ── Jornada · progresso como PDF (print) ──────────────────────────

export async function exportarJornadaProgressoPDF(modulos: ModuloProgressoExport[]): Promise<void> {
  const config = await getObraConfig()
  const totalEtapas = modulos.reduce((sum, m) => sum + m.total, 0)
  const totalConcluidos = modulos.reduce((sum, m) => sum + m.concluidos, 0)
  const pctGeral = totalEtapas > 0 ? Math.round((totalConcluidos / totalEtapas) * 100) : 0

  const secoes = modulos
    .map((m) => {
      const linhas = m.topicos
        .map((t) => {
          const status =
            t.status === 'concluido' ? 'ok' : t.status === 'em-andamento' ? 'andamento' : 'pendente'
          const label =
            t.status === 'concluido'
              ? 'Concluído'
              : t.status === 'em-andamento'
                ? 'Em andamento'
                : 'Pendente'
          return `<tr><td>${escapeHtml(t.titulo)}</td><td><span class="badge badge--${status}">${label}</span></td></tr>`
        })
        .join('')

      return `
      <div class="modulo">
        <h2>${escapeHtml(m.icone)} ${escapeHtml(m.titulo)}</h2>
        <p class="modulo__meta">${m.concluidos}/${m.total} etapas · ${m.pct}% concluído</p>
        <div class="progress" role="progressbar" aria-valuenow="${m.pct}" aria-valuemin="0" aria-valuemax="100">
          <div class="progress__fill" style="width:${m.pct}%"></div>
        </div>
        <table>
          <thead><tr><th>Etapa</th><th>Status</th></tr></thead>
          <tbody>${linhas}</tbody>
        </table>
      </div>`
    })
    .join('')

  const html = `
    <h1>🗺️ Progresso da Jornada</h1>
    <p class="sub">${escapeHtml(config?.nome ?? 'Obra')} · Gerado em ${formatDatetime(new Date().toISOString())}</p>
    <p class="summary-overall">Progresso geral: ${totalConcluidos}/${totalEtapas} etapas (${pctGeral}%)</p>
    ${secoes}`

  abrirJanelaPrint('Progresso da Jornada', html)
}
